import type {
  WorkflowDefinition,
  WorkflowNode,
  WorkflowParameter,
  WorkflowParameterValue,
  WorkflowRunStatus,
  WorkflowRunTarget,
  WorkflowRunValues,
} from "@afar-research/contracts";

/**
 * The rules a Workflow definition must satisfy beyond its schema, and the
 * pure steps of running one. Shared by the Environment Service, which runs
 * Workflows, and the web client, which edits them.
 */

const PLACEHOLDER = /\{\{\s*(params|inputs|outputs)\.([A-Za-z_][A-Za-z0-9_-]*)\s*\}\}/g;
const ANY_PLACEHOLDER = /\{\{\s*([^}]*?)\s*\}\}/g;

/** Every reason a schema-valid definition cannot run, in a stable order; empty when it can. */
export function validateWorkflowDefinition(definition: WorkflowDefinition): string[] {
  const errors: string[] = [];
  const nodes = new Map<string, WorkflowNode>();
  for (const node of definition.nodes) {
    if (nodes.has(node.id)) errors.push(`Node id "${node.id}" is used more than once.`);
    else nodes.set(node.id, node);
  }

  for (const node of nodes.values()) {
    errors.push(...duplicateNames(node, "parameter", node.parameters ?? []));
    errors.push(...duplicateNames(node, "input", node.inputs ?? []));
    errors.push(...duplicateNames(node, "output", node.outputs ?? []));
    for (const parameter of node.parameters ?? []) {
      const error = parameterValueError(parameter, parameter.default);
      if (error) errors.push(`Parameter "${node.id}.${parameter.name}" default: ${error}`);
    }
    errors.push(...placeholderErrors(node));
  }

  const wiredInputs = new Set<string>();
  for (const edge of definition.edges ?? []) {
    const source = nodes.get(edge.from.node);
    const target = nodes.get(edge.to.node);
    const from = `${edge.from.node}.${edge.from.port}`;
    const to = `${edge.to.node}.${edge.to.port}`;
    if (!source?.outputs?.some((port) => port.name === edge.from.port)) {
      errors.push(`Edge source "${from}" is not an output port.`);
    }
    if (!target?.inputs?.some((port) => port.name === edge.to.port)) {
      errors.push(`Edge target "${to}" is not an input port.`);
    }
    if (wiredInputs.has(to)) errors.push(`Input "${to}" has more than one incoming edge.`);
    wiredInputs.add(to);
  }

  const cycle = findCycle(definition);
  if (cycle) errors.push(`The graph has a cycle through: ${cycle.join(", ")}.`);
  return errors;
}

function duplicateNames(
  node: WorkflowNode,
  kind: string,
  entries: ReadonlyArray<{ readonly name: string }>,
): string[] {
  const seen = new Set<string>();
  const errors: string[] = [];
  for (const { name } of entries) {
    if (seen.has(name)) errors.push(`Node "${node.id}" declares ${kind} "${name}" more than once.`);
    seen.add(name);
  }
  return errors;
}

function placeholderErrors(node: WorkflowNode): string[] {
  const declared = {
    params: new Set((node.parameters ?? []).map((entry) => entry.name)),
    inputs: new Set((node.inputs ?? []).map((entry) => entry.name)),
    outputs: new Set((node.outputs ?? []).map((entry) => entry.name)),
  };
  const errors: string[] = [];
  for (const match of node.command.matchAll(ANY_PLACEHOLDER)) {
    const reference = match[1] ?? "";
    const [group, name, ...rest] = reference.split(".");
    const names = declared[group as keyof typeof declared];
    if (!names || name === undefined || rest.length > 0 || !names.has(name)) {
      errors.push(`Node "${node.id}" command uses unknown placeholder "${reference}".`);
    }
  }
  return errors;
}

function upstreamOf(definition: WorkflowDefinition): Map<string, string[]> {
  const upstream = new Map<string, string[]>(definition.nodes.map((node) => [node.id, []]));
  for (const edge of definition.edges ?? []) {
    upstream.get(edge.to.node)?.push(edge.from.node);
  }
  return upstream;
}

function findCycle(definition: WorkflowDefinition): string[] | null {
  const upstream = upstreamOf(definition);
  const state = new Map<string, "visiting" | "done">();
  const path: string[] = [];
  const visit = (id: string): string[] | null => {
    if (state.get(id) === "done") return null;
    if (state.get(id) === "visiting") return path.slice(path.indexOf(id)).toSorted();
    state.set(id, "visiting");
    path.push(id);
    for (const parent of upstream.get(id) ?? []) {
      const cycle = visit(parent);
      if (cycle) return cycle;
    }
    path.pop();
    state.set(id, "done");
    return null;
  };
  for (const node of definition.nodes) {
    const cycle = visit(node.id);
    if (cycle) return cycle;
  }
  return null;
}

/**
 * The nodes a run executes, in dependency order: every node for the whole
 * Workflow, or one node and everything upstream of it. Expects a definition
 * that `validateWorkflowDefinition` accepts.
 */
export function selectWorkflowRunNodes(
  definition: WorkflowDefinition,
  target: WorkflowRunTarget,
): string[] {
  const upstream = upstreamOf(definition);
  const included =
    target.kind === "workflow"
      ? new Set(definition.nodes.map((node) => node.id))
      : ancestorsAndSelf(upstream, target.nodeId);
  const ordered: string[] = [];
  const placed = new Set<string>();
  const place = (id: string) => {
    if (placed.has(id)) return;
    placed.add(id);
    for (const parent of upstream.get(id) ?? []) place(parent);
    if (included.has(id)) ordered.push(id);
  };
  for (const node of definition.nodes) place(node.id);
  return ordered;
}

function ancestorsAndSelf(upstream: Map<string, string[]>, id: string): Set<string> {
  const found = new Set<string>();
  const pending = [id];
  while (pending.length > 0) {
    const next = pending.pop()!;
    if (found.has(next) || !upstream.has(next)) continue;
    found.add(next);
    pending.push(...(upstream.get(next) ?? []));
  }
  return found;
}

function parameterValueError(
  parameter: WorkflowParameter,
  value: WorkflowParameterValue,
): string | null {
  switch (parameter.type) {
    case "number":
    case "integer": {
      if (typeof value !== "number" || !Number.isFinite(value)) return "must be a number.";
      if (parameter.type === "integer" && !Number.isInteger(value)) return "must be an integer.";
      if (parameter.min !== undefined && value < parameter.min) {
        return `must be at least ${parameter.min}.`;
      }
      if (parameter.max !== undefined && value > parameter.max) {
        return `must be at most ${parameter.max}.`;
      }
      return null;
    }
    case "choice":
      return typeof value === "string" && parameter.options.includes(value)
        ? null
        : `must be one of ${parameter.options.join(", ")}.`;
    case "boolean":
      return typeof value === "boolean" ? null : "must be true or false.";
    case "text":
      return typeof value === "string" ? null : "must be text.";
    case "file":
      return value === null || typeof value === "string" ? null : "must be a file path.";
  }
}

/** A node's parameter values for one run: chosen values over declared defaults. */
export function resolveWorkflowNodeParameters(
  node: WorkflowNode,
  chosen: Readonly<Record<string, WorkflowParameterValue>> | undefined,
): { values: Record<string, WorkflowParameterValue>; errors: string[] } {
  const values: Record<string, WorkflowParameterValue> = {};
  const errors: string[] = [];
  for (const parameter of node.parameters ?? []) {
    const value =
      chosen !== undefined && Object.hasOwn(chosen, parameter.name)
        ? chosen[parameter.name]!
        : parameter.default;
    const error = parameterValueError(parameter, value);
    if (error) errors.push(`Parameter "${node.id}.${parameter.name}": ${error}`);
    values[parameter.name] = value;
  }
  return { values, errors };
}

function shellQuote(value: string): string {
  return `'${value.replaceAll("'", "'\\''")}'`;
}

/**
 * Expands a node's command template. Every substituted value is quoted for a
 * POSIX shell, so a parameter can never inject a second command.
 */
export function expandWorkflowCommand(
  template: string,
  values: {
    readonly params: Readonly<Record<string, WorkflowParameterValue>>;
    readonly inputs: Readonly<Record<string, string>>;
    readonly outputs: Readonly<Record<string, string>>;
  },
): string {
  return template.replace(PLACEHOLDER, (_match, group: keyof typeof values, name: string) => {
    const value = values[group][name];
    return shellQuote(value === undefined || value === null ? "" : String(value));
  });
}

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value).toSorted(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${canonicalJson(entry)}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

/**
 * The text whose hash is a node's cache key. Two runs of a node with equal
 * material produce the same outputs, so the later one may reuse them.
 */
export function workflowNodeCacheMaterial(input: {
  readonly command: string;
  readonly parameters: Readonly<Record<string, WorkflowParameterValue>>;
  readonly inputHashes: Readonly<Record<string, string>>;
  readonly codeFileHashes: Readonly<Record<string, string>>;
}): string {
  return canonicalJson({ v: 1, ...input });
}

/** A run that is waiting or executing; any other status is final. */
export function isWorkflowRunActive(status: WorkflowRunStatus): boolean {
  return status === "queued" || status === "running";
}

/** Next-run values with `over` replacing individual parameters and inputs, node by node. */
export function overlayWorkflowRunValues(
  base: WorkflowRunValues,
  over: Partial<WorkflowRunValues>,
): WorkflowRunValues {
  const overlay = <V>(
    left: Readonly<Record<string, Readonly<Record<string, V>>>>,
    right: Readonly<Record<string, Readonly<Record<string, V>>>> | undefined,
  ) => {
    const merged: Record<string, Record<string, V>> = {};
    for (const [node, values] of Object.entries(left)) merged[node] = { ...values };
    for (const [node, values] of Object.entries(right ?? {})) {
      merged[node] = { ...merged[node], ...values };
    }
    return merged;
  };
  return {
    parameters: overlay(base.parameters, over.parameters),
    inputs: overlay(base.inputs, over.inputs),
  };
}
